// Which rules document to show, by language.
//
// One entry per locale, each a *dynamic* import: the rules are a few pages of
// prose that most sessions never open, and a second language should cost a
// file rather than a bigger bundle for everyone who does not read it. This is
// the whole reason the document is MDX and lives here instead of being a
// string in a component.
//
// Adding a language is: drop in `<code>.mdx`, add a line to LOCALES. Nothing
// else in the app needs to know.

import type { ComponentType } from "react";
import type { MDXComponents } from "mdx/types";

export interface Locale {
  code: string;
  /** The language's own name for itself — never the English one. */
  name: string;
  load: () => Promise<{ default: ComponentType<{ components?: MDXComponents }> }>;
}

export const LOCALES: readonly Locale[] = [
  { code: "en", name: "English", load: () => import("./en.mdx") }
];

export const DEFAULT_LOCALE = "en";

/**
 * The best match for a browser's language list, falling back to English.
 *
 * Matches on the primary subtag, so `en-GB` and `en-US` both find `en` — the
 * rules are not going to differ by region, and a reader who gets English
 * because we had no Portuguese is better served than one who gets nothing.
 */
export function pickLocale(preferred: readonly string[]): Locale {
  for (const tag of preferred) {
    const primary = tag.toLowerCase().split("-")[0];
    const found = LOCALES.find((locale) => locale.code === primary);
    if (found) return found;
  }
  return LOCALES.find((locale) => locale.code === DEFAULT_LOCALE) ?? LOCALES[0];
}
